import { useForm } from "react-hook-form";
import EdTextfield from "@/components/forms/EdTextfield";
import { TERipple } from "tw-elements-react";
import EdocertLogo from "@/assets/edocert-logo.svg";
import ExagonLogo from "@/assets/exagon-logo.svg";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useVerifyActivationToken } from "../hooks/useVerifyActivationToken";
import { useActivateCollaborator } from "../hooks/useActivateCollaborator";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { ActivateCollaboratorDto } from "@/core/api/types/collaborators";

export function ActivateCollaboratorPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ActivateCollaboratorDto>({
    mode: "onSubmit",
  });

  const { mutate: verifyToken } = useVerifyActivationToken();
  const { mutate: activateCollaborator, isPending } = useActivateCollaborator();

  const password = watch("password");
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      verifyToken(token, {
        onSuccess: (response) => {
          if (response.valid) {
            toast.success("Token validato con successo");
          } else {
            toast.error("Token non valido");
            navigate("/login");
          }
        },
        onError: () => {
          toast.error("Token non valido");
          navigate("/login");
        },
      });
    }
  }, [token, navigate]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    handleSubmit((data: ActivateCollaboratorDto) => {
      if (token) {
        activateCollaborator(
          {
            token,
            ...data,
          },
          {
            onSuccess: () => {
              navigate("/login");
            },
          }
        );
      }
    })(e);
  };

  return (
    <div className="flex min-h-screen items-start pt-[170px] justify-center bg-gray-100 w-screen relative">
      <div className="absolute top-0 z-0 w-full h-1/2 bg-red-800"></div>
      <div className="w-full max-w-[658px] max-h-[502px] box-border z-30">
        <form
          onSubmit={handleFormSubmit}
          noValidate
          className="bg-white shadow-md rounded-lg px-8 md:px-[120px] mx-4 md:mx-0 pb-14 pt-10"
        >
          <div className="flex justify-center mb-8">
            <img src={EdocertLogo} className="w-[260px]" alt="Edocert Logo" />
          </div>
          <div className="flex flex-col gap-2 mb-5 w-full">
            <span className="text-[#4F4F4F] text-sm font-bold">
              Attiva il tuo account
            </span>
            <span className="text-[#4F4F4F] text-sm">
              Inserisci la password per attivare il tuo account
            </span>
          </div>

          <div className="mb-4">
            <EdTextfield
              label="Password"
              type="password"
              id="password"
              placeholder="Inserisci la password"
              register={register("password", {
                required: "Campo obbligatorio",
                minLength: {
                  value: 8,
                  message: "La password deve essere di almeno 8 caratteri",
                },
              })}
              error={errors.password && errors.password.message}
            />
          </div>

          <div className="mb-4">
            <EdTextfield
              label="Conferma password"
              type="password"
              id="passwordConfirmation"
              placeholder="Conferma la password"
              register={register("passwordConfirmation", {
                required: "Campo obbligatorio",
                validate: (value) =>
                  value === password || "Le password non coincidono",
              })}
              error={
                errors.passwordConfirmation &&
                errors.passwordConfirmation.message
              }
            />
          </div>

          <div className="flex flex-col gap-4">
            <TERipple rippleColor="light" className="w-full">
              <button
                type="submit"
                disabled={isPending || !token}
                className="w-full rounded bg-red-700 px-6 pb-2.5 pt-3 text-xs font-medium uppercase leading-normal text-white transition duration-150 ease-in-out hover:bg-red-800 disabled:opacity-50"
              >
                {isPending ? "Caricamento..." : "Attiva Account"}
              </button>
            </TERipple>
          </div>

          <div className="flex justify-center items-center mt-8 mb-6 w-full text-center">
            <div className="flex w-full text-sm text-center">
              <Link
                to="/login"
                className="w-full font-medium text-center text-edPrimaryColor-500 hover:text-red-800"
              >
                Torna al login
              </Link>
            </div>
          </div>

          <div className="flex justify-center">
            <img src={ExagonLogo} className="" alt="Exagon Logo" />
          </div>
        </form>
      </div>
    </div>
  );
}
